/**
 * React Query Provider Configuration
 * Configures TanStack Query v5 with optimal defaults for the Mensova application
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ReactNode, useState } from 'react';
import { isDevelopment } from '@/utils/env';

/**
 * Default query client configuration
 * Optimized for Mensova application requirements
 */
const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: 5 minutes
        // Data is considered fresh for 5 minutes before refetching
        staleTime: 5 * 60 * 1000,

        // Cache time (gcTime in v5): 30 minutes
        // Cached data is garbage collected after 30 minutes of being unused
        gcTime: 30 * 60 * 1000,

        // Retry failed requests 3 times with exponential backoff
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Don't refetch on window focus (can be enabled per-query if needed)
        refetchOnWindowFocus: false,

        // Refetch on reconnect (network recovery)
        refetchOnReconnect: true,

        // Don't refetch on mount if data is fresh
        refetchOnMount: false,
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
        retryDelay: 1000,
      },
    },
  });

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query Provider Component
 * Wraps the application with React Query context
 * Includes DevTools in development mode
 */
export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  // Create query client instance once per component instance
  // This ensures SSR compatibility if needed in the future
  const [queryClient] = useState(() => createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools only in development */}
      {isDevelopment() && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

/**
 * Export the createQueryClient function for testing purposes
 */
export { createQueryClient };
